import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { LearningAnalytics } from '@coffeeplexity/shared';
import { LineChart, BarChart } from 'react-native-chart-kit';

const { width } = Dimensions.get('window');

interface EnhancedAnalyticsInsightsProps {
  analytics: LearningAnalytics;
}

export const EnhancedAnalyticsInsights: React.FC<
  EnhancedAnalyticsInsightsProps
> = ({ analytics }) => {
  const [selectedMetric, setSelectedMetric] = useState<
    'velocity' | 'engagement' | 'retention' | 'efficiency'
  >('velocity');

  const renderLearningVelocityInsights = () => {
    const { learningVelocity } = analytics;

    const velocityData = {
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      datasets: [
        {
          data: [
            learningVelocity.averageRate * 0.8,
            learningVelocity.averageRate * 0.9,
            learningVelocity.currentRate,
            learningVelocity.predictedNextWeek,
          ],
          color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
          strokeWidth: 3,
        },
      ],
    };

    return (
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <Ionicons name='trending-up' size={24} color='#6750A4' />
          <Text style={styles.insightTitle}>Learning Velocity Analysis</Text>
        </View>

        <View style={styles.velocityMetrics}>
          <View style={styles.velocityMetric}>
            <Text style={styles.metricValue}>
              {learningVelocity.currentRate.toFixed(1)}
            </Text>
            <Text style={styles.metricLabel}>Current Rate</Text>
            <Text style={styles.metricUnit}>skills/week</Text>
          </View>
          <View style={styles.velocityMetric}>
            <Text style={styles.metricValue}>
              {learningVelocity.predictedNextWeek.toFixed(1)}
            </Text>
            <Text style={styles.metricLabel}>Predicted</Text>
            <Text style={styles.metricUnit}>next week</Text>
          </View>
          <View style={styles.velocityMetric}>
            <Text style={styles.metricValue}>
              {learningVelocity.peakRate.toFixed(1)}
            </Text>
            <Text style={styles.metricLabel}>Peak Rate</Text>
            <Text style={styles.metricUnit}>achieved</Text>
          </View>
        </View>

        <LineChart
          data={velocityData}
          width={width - 80}
          height={180}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 1,
            color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
            propsForDots: {
              r: '4',
              strokeWidth: '2',
              stroke: '#6750A4',
            },
          }}
          bezier
          style={styles.chart}
        />

        <View style={styles.trendIndicator}>
          <Ionicons
            name={
              learningVelocity.trendDirection === 'accelerating'
                ? 'trending-up'
                : learningVelocity.trendDirection === 'decelerating'
                  ? 'trending-down'
                  : 'remove'
            }
            size={16}
            color={
              learningVelocity.trendDirection === 'accelerating'
                ? '#10B981'
                : learningVelocity.trendDirection === 'decelerating'
                  ? '#EF4444'
                  : '#6B7280'
            }
          />
          <Text style={styles.trendText}>
            Learning velocity is {learningVelocity.trendDirection}
          </Text>
        </View>

        <View style={styles.influencingFactors}>
          <Text style={styles.factorsTitle}>Key Influencing Factors</Text>
          {learningVelocity.factorsInfluencing
            .slice(0, 2)
            .map((factor, index) => (
              <View key={index} style={styles.factorItem}>
                <View style={styles.factorHeader}>
                  <Text style={styles.factorName}>{factor.factor}</Text>
                  <View style={styles.factorImpact}>
                    <Text
                      style={[
                        styles.impactValue,
                        {
                          color: factor.impact > 0 ? '#10B981' : '#EF4444',
                        },
                      ]}
                    >
                      {factor.impact > 0 ? '+' : ''}
                      {Math.round(factor.impact * 100)}%
                    </Text>
                  </View>
                </View>
                <Text style={styles.factorDescription}>
                  {factor.description}
                </Text>
                <View style={styles.confidenceBar}>
                  <View
                    style={[
                      styles.confidenceFill,
                      { width: `${factor.confidence * 100}%` },
                    ]}
                  />
                </View>
              </View>
            ))}
        </View>
      </View>
    );
  };

  const renderEngagementAnalysis = () => {
    const { engagementMetrics } = analytics;

    const engagementData = {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [
        {
          data: [25, 30, 28, 35, 32, 20, 15], // Mock daily engagement data
          color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
          strokeWidth: 2,
        },
      ],
    };

    return (
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <Ionicons name='pulse' size={24} color='#10B981' />
          <Text style={styles.insightTitle}>Engagement Pattern Analysis</Text>
        </View>

        <View style={styles.engagementOverview}>
          <View style={styles.engagementStat}>
            <Text style={styles.statValue}>
              {engagementMetrics.dailyActiveMinutes}
            </Text>
            <Text style={styles.statLabel}>Daily Minutes</Text>
          </View>
          <View style={styles.engagementStat}>
            <Text style={styles.statValue}>
              {engagementMetrics.sessionFrequency.toFixed(1)}
            </Text>
            <Text style={styles.statLabel}>Sessions/Week</Text>
          </View>
          <View style={styles.engagementStat}>
            <Text style={styles.statValue}>
              {engagementMetrics.completionRate}%
            </Text>
            <Text style={styles.statLabel}>Completion</Text>
          </View>
        </View>

        <LineChart
          data={engagementData}
          width={width - 80}
          height={160}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
          }}
          bezier
          style={styles.chart}
        />

        <View style={styles.engagementInsights}>
          <View style={styles.insightItem}>
            <Ionicons name='time' size={16} color='#F59E0B' />
            <Text style={styles.insightText}>
              Peak engagement: Tuesday-Thursday mornings
            </Text>
          </View>
          <View style={styles.insightItem}>
            <Ionicons name='trending-up' size={16} color='#10B981' />
            <Text style={styles.insightText}>
              Motivation level: {engagementMetrics.motivationLevel}% (High)
            </Text>
          </View>
          <View style={styles.insightItem}>
            <Ionicons name='heart' size={16} color='#EF4444' />
            <Text style={styles.insightText}>
              Satisfaction score: {engagementMetrics.satisfactionScore}%
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderRetentionAnalysis = () => {
    const { retentionAnalysis } = analytics;

    return (
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <Ionicons name='shield-checkmark' size={24} color='#8B5CF6' />
          <Text style={styles.insightTitle}>Retention Risk Analysis</Text>
        </View>

        <View style={styles.retentionOverview}>
          <View style={styles.retentionMetric}>
            <Text style={styles.retentionValue}>
              {retentionAnalysis.retentionRate}%
            </Text>
            <Text style={styles.retentionLabel}>Retention Rate</Text>
            <View style={styles.retentionIndicator}>
              <Ionicons name='checkmark-circle' size={16} color='#10B981' />
              <Text style={styles.retentionStatus}>Excellent</Text>
            </View>
          </View>

          <View style={styles.riskAssessment}>
            <Text style={styles.riskTitle}>Churn Risk</Text>
            <View style={styles.riskMeter}>
              <View style={styles.riskBackground}>
                <View
                  style={[
                    styles.riskFill,
                    {
                      width: `${retentionAnalysis.churnRisk}%`,
                      backgroundColor:
                        retentionAnalysis.churnRisk < 20
                          ? '#10B981'
                          : retentionAnalysis.churnRisk < 50
                            ? '#F59E0B'
                            : '#EF4444',
                    },
                  ]}
                />
              </View>
              <Text style={styles.riskPercentage}>
                {retentionAnalysis.churnRisk}%
              </Text>
            </View>
            <Text style={styles.riskLevel}>
              {retentionAnalysis.churnRisk < 20
                ? 'Low Risk'
                : retentionAnalysis.churnRisk < 50
                  ? 'Medium Risk'
                  : 'High Risk'}
            </Text>
          </View>
        </View>

        <View style={styles.retentionFactors}>
          <Text style={styles.factorsTitle}>Retention Factors</Text>
          {retentionAnalysis.retentionFactors.map((factor, index) => (
            <View key={index} style={styles.retentionFactor}>
              <View style={styles.factorRow}>
                <Text style={styles.factorName}>{factor.factor}</Text>
                <View style={styles.factorImpactBadge}>
                  <Ionicons
                    name={factor.impact > 0 ? 'arrow-up' : 'arrow-down'}
                    size={12}
                    color={factor.impact > 0 ? '#10B981' : '#EF4444'}
                  />
                  <Text
                    style={[
                      styles.factorImpactText,
                      { color: factor.impact > 0 ? '#10B981' : '#EF4444' },
                    ]}
                  >
                    {Math.abs(Math.round(factor.impact * 100))}%
                  </Text>
                </View>
              </View>
              <View style={styles.factorConfidence}>
                <Text style={styles.confidenceLabel}>
                  Confidence: {Math.round(factor.confidence * 100)}%
                </Text>
                {factor.actionable && (
                  <View style={styles.actionableBadge}>
                    <Text style={styles.actionableText}>Actionable</Text>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>

        {retentionAnalysis.interventionSuggestions.length > 0 && (
          <View style={styles.interventions}>
            <Text style={styles.interventionsTitle}>
              Suggested Interventions
            </Text>
            {retentionAnalysis.interventionSuggestions
              .slice(0, 3)
              .map((suggestion, index) => (
                <View key={index} style={styles.interventionItem}>
                  <Ionicons name='bulb' size={14} color='#F59E0B' />
                  <Text style={styles.interventionText}>{suggestion}</Text>
                </View>
              ))}
          </View>
        )}
      </View>
    );
  };

  const renderEfficiencyAnalysis = () => {
    const { overallProgress, learningVelocity } = analytics;

    const efficiencyData = {
      labels: ['Focus', 'Practice', 'Review', 'Apply'],
      datasets: [
        {
          data: [85, 78, 92, 73], // Mock efficiency scores by activity type
        },
      ],
    };

    return (
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <Ionicons name='speedometer' size={24} color='#F59E0B' />
          <Text style={styles.insightTitle}>Learning Efficiency Breakdown</Text>
        </View>

        <View style={styles.efficiencyScore}>
          <Text style={styles.efficiencyValue}>
            {overallProgress.efficiencyScore}
          </Text>
          <Text style={styles.efficiencyLabel}>Overall Efficiency Score</Text>
          <Text style={styles.efficiencyDescription}>
            {overallProgress.efficiencyScore >= 85
              ? 'Excellent efficiency'
              : overallProgress.efficiencyScore >= 70
                ? 'Good efficiency'
                : 'Room for improvement'}
          </Text>
        </View>

        <BarChart
          data={efficiencyData}
          width={width - 80}
          height={180}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(245, 158, 11, ${opacity})`,
            labelColor: (opacity = 1) => `rgba(107, 114, 128, ${opacity})`,
            style: { borderRadius: 16 },
          }}
          style={styles.chart}
        />

        <View style={styles.efficiencyInsights}>
          <View style={styles.efficiencyInsight}>
            <Ionicons name='checkmark-circle' size={16} color='#10B981' />
            <Text style={styles.insightText}>
              Review sessions are most efficient (92%)
            </Text>
          </View>
          <View style={styles.efficiencyInsight}>
            <Ionicons name='warning' size={16} color='#F59E0B' />
            <Text style={styles.insightText}>
              Application phase needs improvement (73%)
            </Text>
          </View>
          <View style={styles.efficiencyInsight}>
            <Ionicons name='time' size={16} color='#6750A4' />
            <Text style={styles.insightText}>
              Optimal session length:{' '}
              {Math.round(
                overallProgress.timeInvested /
                  overallProgress.totalSkillsLearned
              )}{' '}
              min/skill
            </Text>
          </View>
        </View>

        <View style={styles.optimizationSuggestions}>
          <Text style={styles.suggestionsTitle}>Optimization Suggestions</Text>
          {learningVelocity.optimizationSuggestions
            .slice(0, 2)
            .map((suggestion, index) => (
              <View key={index} style={styles.suggestionItem}>
                <Ionicons name='arrow-forward' size={14} color='#6750A4' />
                <Text style={styles.suggestionText}>{suggestion}</Text>
              </View>
            ))}
        </View>
      </View>
    );
  };

  const renderContent = () => {
    switch (selectedMetric) {
      case 'velocity':
        return renderLearningVelocityInsights();
      case 'engagement':
        return renderEngagementAnalysis();
      case 'retention':
        return renderRetentionAnalysis();
      case 'efficiency':
        return renderEfficiencyAnalysis();
      default:
        return renderLearningVelocityInsights();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Enhanced Analytics Insights</Text>
        <Text style={styles.subtitle}>
          Deep dive into your learning patterns with AI-powered analysis
        </Text>
      </View>

      <View style={styles.metricSelector}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.selectorContent}
        >
          {[
            { id: 'velocity', title: 'Velocity', icon: 'trending-up' },
            { id: 'engagement', title: 'Engagement', icon: 'pulse' },
            { id: 'retention', title: 'Retention', icon: 'shield-checkmark' },
            { id: 'efficiency', title: 'Efficiency', icon: 'speedometer' },
          ].map(metric => (
            <TouchableOpacity
              key={metric.id}
              style={[
                styles.metricButton,
                selectedMetric === metric.id && styles.activeMetricButton,
              ]}
              onPress={() => setSelectedMetric(metric.id as any)}
            >
              <Ionicons
                name={metric.icon as any}
                size={18}
                color={selectedMetric === metric.id ? '#FFFFFF' : '#6750A4'}
              />
              <Text
                style={[
                  styles.metricButtonText,
                  selectedMetric === metric.id && styles.activeMetricButtonText,
                ]}
              >
                {metric.title}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderContent()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  metricSelector: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 12,
  },
  selectorContent: {
    paddingHorizontal: 20,
    gap: 8,
  },
  metricButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    gap: 6,
  },
  activeMetricButton: {
    backgroundColor: '#6750A4',
    borderColor: '#6750A4',
  },
  metricButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6750A4',
  },
  activeMetricButtonText: {
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  insightCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  insightTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  velocityMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
  },
  velocityMetric: {
    alignItems: 'center',
  },
  metricValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#6750A4',
  },
  metricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  metricUnit: {
    fontSize: 10,
    color: '#9CA3AF',
    marginTop: 1,
  },
  chart: {
    borderRadius: 16,
    marginVertical: 16,
  },
  trendIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    gap: 8,
  },
  trendText: {
    fontSize: 14,
    color: '#4B5563',
    fontWeight: '500',
  },
  influencingFactors: {
    marginTop: 8,
  },
  factorsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  factorItem: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  factorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  factorName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
    flex: 1,
  },
  factorImpact: {
    alignItems: 'center',
  },
  impactValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  factorDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
    marginBottom: 8,
  },
  confidenceBar: {
    height: 4,
    backgroundColor: '#E5E7EB',
    borderRadius: 2,
    overflow: 'hidden',
  },
  confidenceFill: {
    height: '100%',
    backgroundColor: '#6750A4',
    borderRadius: 2,
  },
  engagementOverview: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
  },
  engagementStat: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#10B981',
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  engagementInsights: {
    marginTop: 16,
    gap: 8,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  insightText: {
    fontSize: 14,
    color: '#4B5563',
    flex: 1,
  },
  retentionOverview: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  retentionMetric: {
    flex: 1,
    alignItems: 'center',
  },
  retentionValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#8B5CF6',
  },
  retentionLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
    marginBottom: 8,
  },
  retentionIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  retentionStatus: {
    fontSize: 12,
    fontWeight: '500',
    color: '#10B981',
  },
  riskAssessment: {
    flex: 1,
    alignItems: 'center',
  },
  riskTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  riskMeter: {
    width: 80,
    alignItems: 'center',
    marginBottom: 8,
  },
  riskBackground: {
    width: '100%',
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 4,
  },
  riskFill: {
    height: '100%',
    borderRadius: 4,
  },
  riskPercentage: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
  },
  riskLevel: {
    fontSize: 12,
    color: '#6B7280',
  },
  retentionFactors: {
    marginBottom: 16,
  },
  retentionFactor: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  factorRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  factorImpactBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  factorImpactText: {
    fontSize: 12,
    fontWeight: '600',
  },
  factorConfidence: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  confidenceLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  actionableBadge: {
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  actionableText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#16A34A',
  },
  interventions: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 12,
  },
  interventionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#92400E',
    marginBottom: 8,
  },
  interventionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 4,
  },
  interventionText: {
    fontSize: 12,
    color: '#92400E',
    flex: 1,
    lineHeight: 16,
  },
  efficiencyScore: {
    alignItems: 'center',
    marginBottom: 20,
  },
  efficiencyValue: {
    fontSize: 32,
    fontWeight: '700',
    color: '#F59E0B',
  },
  efficiencyLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 4,
  },
  efficiencyDescription: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  efficiencyInsights: {
    marginTop: 16,
    gap: 8,
  },
  efficiencyInsight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  optimizationSuggestions: {
    backgroundColor: '#EDE7F6',
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
  },
  suggestionsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6750A4',
    marginBottom: 8,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 4,
  },
  suggestionText: {
    fontSize: 12,
    color: '#6750A4',
    flex: 1,
    lineHeight: 16,
  },
});
